import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Chip,
} from '@mui/material';
import {
    DeleteOutline as DeleteIcon,
    ExpandMore as ExpandIcon,
    ChevronRight as ChevronIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import timesheetService from '../../../services/timesheetService';

function formatDay(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

// scope:
//   'week' — show all weekRecords grouped by day → job → employee
//   'day'  — show single-day records grouped by job → employee
export default function ClearTimesheetModal({
    open,
    onClose,
    scope,            // 'week' | 'day'
    currentDate,
    weekInfo,
    weekRecords,
    jobs,
    employees,
    onDeleteRecords,  // (clientIds: string[]) => Promise<void>
    onClearAll,       // () => Promise<void>
}) {
    const [expandedDays, setExpandedDays] = useState(new Set());
    const [expandedJobs, setExpandedJobs] = useState(new Set());

    const activeRecords = useMemo(() => {
        // Только СВОИ записи: в локальной базе лежат и часы других форманов по тем же
        // сотрудникам (их приносит sync) — предлагать их к удалению нельзя.
        const myUserId = timesheetService.getCurrentUserMeta().userId;
        const base = weekRecords.filter(r => !r.isDeleted && r.hours > 0
            && (myUserId == null || Number(r.createdByUserId) === Number(myUserId)));
        return scope === 'day' ? base.filter(r => r.workDate === currentDate) : base;
    }, [weekRecords, scope, currentDate]);

    // Group records: day → job → [records]
    const tree = useMemo(() => {
        const byDay = new Map();
        for (const r of activeRecords) {
            if (!byDay.has(r.workDate)) byDay.set(r.workDate, { hours: 0, byJob: new Map() });
            const d = byDay.get(r.workDate);
            d.hours += r.hours;
            if (!d.byJob.has(r.jobId)) d.byJob.set(r.jobId, { hours: 0, records: [] });
            const j = d.byJob.get(r.jobId);
            j.hours += r.hours;
            j.records.push(r);
        }
        return [...byDay.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    }, [activeRecords]);

    const totalHours = activeRecords.reduce((s, r) => s + r.hours, 0);

    const toggleDay = (workDate) => {
        setExpandedDays(prev => {
            const next = new Set(prev);
            if (next.has(workDate)) next.delete(workDate);
            else next.add(workDate);
            return next;
        });
    };
    const toggleJob = (key) => {
        setExpandedJobs(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Swal by default renders below MUI Dialog (z-index 1300). Force it above.
    const swalAboveDialog = {
        backdrop: true,
        customClass: { container: 'swal-above-dialog' },
        didOpen: (el) => {
            const container = el.closest('.swal2-container') || document.querySelector('.swal2-container');
            if (container) container.style.zIndex = '2000';
        },
    };

    const confirmAndDelete = async (recordIds, summary) => {
        const result = await Swal.fire({
            title: 'Delete?',
            html: summary,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#757575',
            confirmButtonText: 'Delete',
            ...swalAboveDialog,
        });
        if (result.isConfirmed) {
            await onDeleteRecords(recordIds);
        }
    };

    const handleDeleteDay = (workDate, dayInfo) => {
        const ids = [];
        for (const [, jobInfo] of dayInfo.byJob) {
            for (const r of jobInfo.records) ids.push(r.clientId);
        }
        confirmAndDelete(ids, `Delete <strong>${formatDay(workDate)}</strong> (${dayInfo.hours}h, ${ids.length} records)?`);
    };

    const handleDeleteJob = (workDate, jobId, jobInfo) => {
        const job = jobs.find(j => j.id === jobId);
        const code = timesheetService.getJobCode(job) || jobId;
        const ids = jobInfo.records.map(r => r.clientId);
        confirmAndDelete(ids, `Delete <strong>#${code}</strong> from <strong>${formatDay(workDate)}</strong> (${jobInfo.hours}h, ${ids.length} records)?`);
    };

    const handleDeleteRecord = (record) => {
        const emp = employees.find(e => e.id === record.employeeId);
        const empName = timesheetService.getEmployeeName(emp) || 'Unknown';
        const job = jobs.find(j => j.id === record.jobId);
        const code = timesheetService.getJobCode(job) || record.jobId;
        confirmAndDelete([record.clientId], `Delete <strong>${empName}</strong> on <strong>#${code}</strong> (${record.hours}h)?`);
    };

    const handleClearAll = async () => {
        const escapeHtml = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

        const scopeLabel = scope === 'day' ? formatDay(currentDate) : `Week ${weekInfo?.weekNumber || ''}`;

        // Build a compact breakdown so the user sees exactly what will be deleted.
        const dayBlocks = tree.map(([workDate, dayInfo]) => {
            const jobRows = [...dayInfo.byJob.entries()].map(([jobId, jobInfo]) => {
                const job = jobs.find(j => j.id === jobId);
                const code = escapeHtml(timesheetService.getJobCode(job) || String(jobId));
                const name = escapeHtml(timesheetService.getJobName(job) || '');
                return `<div style="display:flex;justify-content:space-between;padding:2px 0 2px 10px;font-size:12px;gap:6px;">
                    <span style="min-width:0;overflow:hidden;">
                        <span style="color:#1976d2;font-weight:700;">#${code}</span>
                        ${name ? `<span style="color:#888;margin-left:6px;">${name}</span>` : ''}
                    </span>
                    <span style="color:#c62828;font-weight:600;flex-shrink:0;">${jobInfo.hours}h <small style="color:#888;font-weight:400;">(${jobInfo.records.length})</small></span>
                </div>`;
            }).join('');

            const dayHeader = scope === 'week'
                ? `<div style="display:flex;justify-content:space-between;padding:4px 6px;background:#f5f5f5;border-radius:4px;font-size:13px;margin:4px 0 2px;">
                    <strong>${escapeHtml(formatDay(workDate))}</strong>
                    <span style="color:#c62828;font-weight:700;">${dayInfo.hours}h</span>
                </div>`
                : '';
            return dayHeader + jobRows;
        }).join('');

        const result = await Swal.fire({
            title: `Clear ${scope === 'day' ? 'Day' : 'Week'}?`,
            html: `<div style="text-align:left;">
                <div style="margin-bottom:8px;font-size:13px;color:#444;">
                    Delete <strong>ALL</strong> timesheet data for <strong>${escapeHtml(scopeLabel)}</strong>?
                </div>
                <div style="display:flex;justify-content:space-between;padding:6px 10px;background:#ffebee;border-radius:6px;font-size:13px;margin-bottom:8px;">
                    <span><strong>${activeRecords.length}</strong> record${activeRecords.length === 1 ? '' : 's'} · ${tree.length} day${tree.length === 1 ? '' : 's'}</span>
                    <span style="font-weight:700;color:#c62828;">${totalHours}h</span>
                </div>
                <div style="max-height:260px;overflow:auto;border:1px solid #eee;border-radius:6px;padding:4px 6px;background:#fff;">${dayBlocks}</div>
            </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#757575',
            confirmButtonText: 'Yes, delete all',
            width: 500,
            ...swalAboveDialog,
        });
        if (result.isConfirmed) {
            await onClearAll();
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
            <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    Clear {scope === 'day' ? formatDay(currentDate) : `Week ${weekInfo?.weekNumber || ''}`}
                    <Typography variant="caption" display="block" color="text.secondary">
                        Tap a row to expand · tap 🗑 to delete part
                    </Typography>
                </Box>
                <IconButton onClick={onClose} edge="end"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0, bgcolor: '#f9f9f9' }}>
                {activeRecords.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">No records to delete</Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{
                            px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            bgcolor: '#ffebee', borderBottom: '1px solid #eee',
                        }}>
                            <Typography variant="body2">
                                <strong>{activeRecords.length}</strong> records · {tree.length} day{tree.length === 1 ? '' : 's'}
                            </Typography>
                            <Chip label={`${totalHours}h total`} size="small" color="error" />
                        </Box>

                        <Box sx={{ maxHeight: '55vh', overflow: 'auto' }}>
                            {tree.map(([workDate, dayInfo]) => {
                                const dayOpen = scope === 'day' ? true : expandedDays.has(workDate);
                                const jobCount = dayInfo.byJob.size;
                                return (
                                    <Box key={workDate} sx={{ borderBottom: '1px solid #eee' }}>
                                        {/* Day row — only show header when in week scope */}
                                        {scope === 'week' && (
                                            <Box sx={{
                                                display: 'flex', alignItems: 'center', gap: 1,
                                                px: 1.5, py: 1, bgcolor: '#fff',
                                            }}>
                                                <IconButton size="small" onClick={() => toggleDay(workDate)} sx={{ p: 0.25 }}>
                                                    {dayOpen ? <ExpandIcon /> : <ChevronIcon />}
                                                </IconButton>
                                                <Box sx={{ flex: 1, cursor: 'pointer' }} onClick={() => toggleDay(workDate)}>
                                                    <Typography variant="body2" fontWeight={600}>{formatDay(workDate)}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {jobCount} job{jobCount === 1 ? '' : 's'} · {dayInfo.hours}h
                                                    </Typography>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteDay(workDate, dayInfo)}
                                                    title="Delete entire day"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}

                                        {/* Jobs in this day */}
                                        {dayOpen && [...dayInfo.byJob.entries()].map(([jobId, jobInfo]) => {
                                            const jobKey = `${workDate}|${jobId}`;
                                            const jobOpen = expandedJobs.has(jobKey);
                                            const job = jobs.find(j => j.id === jobId);
                                            const code = timesheetService.getJobCode(job) || jobId;
                                            const name = timesheetService.getJobName(job) || '';
                                            const empCount = jobInfo.records.length;
                                            return (
                                                <Box key={jobId}>
                                                    <Box sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1,
                                                        px: 1.5, py: 0.75, pl: scope === 'week' ? 4 : 1.5,
                                                        bgcolor: '#fafafa', borderTop: '1px solid #f0f0f0',
                                                    }}>
                                                        <IconButton size="small" onClick={() => toggleJob(jobKey)} sx={{ p: 0.25 }}>
                                                            {jobOpen ? <ExpandIcon /> : <ChevronIcon />}
                                                        </IconButton>
                                                        <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => toggleJob(jobKey)}>
                                                            <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 700 }}>
                                                                #{code}
                                                            </Typography>
                                                            {name && (
                                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                                    {name}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography variant="caption" color="error.main" fontWeight={700}>
                                                            {jobInfo.hours}h
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            ({empCount})
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteJob(workDate, jobId, jobInfo)}
                                                            title="Delete this job for this day"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>

                                                    {/* Employees */}
                                                    {jobOpen && jobInfo.records.map(record => {
                                                        const emp = employees.find(e => e.id === record.employeeId);
                                                        const empName = timesheetService.getEmployeeName(emp) || 'Unknown';
                                                        return (
                                                            <Box key={record.clientId} sx={{
                                                                display: 'flex', alignItems: 'center', gap: 1,
                                                                px: 1.5, py: 0.5, pl: scope === 'week' ? 6 : 4,
                                                                bgcolor: '#fff',
                                                            }}>
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography variant="body2" noWrap>{empName}</Typography>
                                                                </Box>
                                                                <Typography variant="caption" color="error.main" fontWeight={600}>
                                                                    {record.hours}h
                                                                </Typography>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleDeleteRecord(record)}
                                                                    title="Delete this entry"
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button
                    onClick={handleClearAll}
                    disabled={activeRecords.length === 0}
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                >
                    Clear All
                </Button>
            </DialogActions>
        </Dialog>
    );
}
